import React, { useMemo } from 'react'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { blue, orange } from '@mui/material/colors'
import { type StrategyDataGridRow } from '../../utils/view-specific/strategy/strategy-utils'
import FilterExportGridToolbar, {
  type FilterExportGridToolbarProps,
} from '../../components/data-grid/filter-export-grid-toolbar'
import { MAIN_SUMMARY_COLUMNS } from './strategy-component-main-summary-col-definitions'
import { Divider, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { getProductTypeColour } from '../../utils/colours-utils'
import { type MainSummaryData } from '../../hooks/strategy/use-main-summary-data-grid-worker'

// Augment the props for the toolbar slot
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends FilterExportGridToolbarProps {
    // Intentionally left empty
  }
}

export interface StrategyComponentMainSummaryProps {
  dataGridRows: StrategyDataGridRow[]
  mainSummaryData: MainSummaryData
  csvBaseFileName: string
}

const StrategyComponentMainSummary: React.FC<StrategyComponentMainSummaryProps> = (props) => {
  const { dataGridRows, mainSummaryData, csvBaseFileName } = props

  const header = useMemo(() => {
    return (
      <Grid container justifyContent='space-between' alignItems='baseline'>
        <Grid item display='flex' gap={1} flexShrink={0}>
          <Typography color='secondary'>Volatility:</Typography>
          <Typography>{mainSummaryData.volatility}</Typography>
        </Grid>
        <Grid item display='flex' gap={1} flexShrink={0}>
          <Typography color='secondary'>Traded Volume:</Typography>
          <Typography>{mainSummaryData.tradedVolume}</Typography>
        </Grid>
        <Grid item display='flex' gap={1} flexShrink={0}>
          <Tooltip title='Total Exposure'>
            <Typography color='secondary'>T.Exp:</Typography>
          </Tooltip>
          <Typography>{mainSummaryData.totalExposure}</Typography>
        </Grid>
        <Grid item display='flex' gap={1} flexShrink={0} alignItems='baseline'>
          <Tooltip title='Realized Exposure'>
            <Typography color='secondary'>R.Exp:</Typography>
          </Tooltip>
          <Typography fontSize='large' fontWeight='bold'>
            {mainSummaryData.realizedExposure}
          </Typography>
        </Grid>
        <Grid item display='flex' gap={1} flexShrink={0}>
          <Typography color='secondary'>T.Pred Fund.:</Typography>
          <Typography>{mainSummaryData.totalPredFunding}</Typography>
        </Grid>
      </Grid>
    )
  }, [mainSummaryData])

  const footer = useMemo(() => {
    const bestAggBidSymbolNodes = mainSummaryData.bestAggBid.split('|').map(symbol => {
      return (
        <Typography
          key={`best-agg-bid-symbol-${symbol}`}
          sx={{ color: getProductTypeColour(symbol) }}
        >
          {symbol}
        </Typography>
      )
    })
    const bestPassiveBidSymbolNodes = mainSummaryData.bestPassiveBid.split('|').map(symbol => {
      return (
        <Typography
          key={`best-passive-bid-symbol-${symbol}`}
          sx={{ color: getProductTypeColour(symbol) }}
        >
          {symbol}
        </Typography>
      )
    })

    const bestAggBidValue = Number.isNaN(mainSummaryData.bestAggBidValue) ? '-' : mainSummaryData.bestAggBidValue
    const bestPassiveBidValue = Number.isNaN(mainSummaryData.bestPassiveBidValue) ? '-' : mainSummaryData.bestPassiveBidValue
    const refBid = mainSummaryData.refBid
    const voidBid = mainSummaryData.voidBid

    const calculatedBestAggMidValue = ((mainSummaryData.bestAggBidValue + mainSummaryData.bestAggAskValue) / 2)
    const midAggValue = Number.isNaN(mainSummaryData.bestAggMidValue)
      ? Number.isNaN(calculatedBestAggMidValue) ? '-' : calculatedBestAggMidValue
      : mainSummaryData.bestAggMidValue
    const calculatedPassiveMidValue = ((mainSummaryData.bestPassiveBidValue + mainSummaryData.bestPassiveAskValue) / 2)
    const midPassiveValue = Number.isNaN(mainSummaryData.bestPassiveMidValue)
      ? Number.isNaN(calculatedPassiveMidValue) ? '-' : calculatedPassiveMidValue
      : mainSummaryData.bestPassiveMidValue

    const bestAggAskValue = Number.isNaN(mainSummaryData.bestAggAskValue) ? '-' : mainSummaryData.bestAggAskValue
    const bestPassiveAskValue = Number.isNaN(mainSummaryData.bestPassiveAskValue) ? '-' : mainSummaryData.bestPassiveAskValue
    const refAsk = mainSummaryData.refAsk
    const voidAsk = mainSummaryData.voidAsk

    const bestAggAskSymbolNodes = mainSummaryData.bestAggAsk.split('|').map(symbol => {
      return (
        <Typography
          key={`best-agg-ask-symbol-${symbol}`}
          sx={{ color: getProductTypeColour(symbol) }}
        >
          {symbol}
        </Typography>
      )
    })
    const bestPassiveAskSymbolNodes = mainSummaryData.bestPassiveAsk.split('|').map(symbol => {
      return (
        <Typography
          key={`best-passive-ask-symbol-${symbol}`}
          sx={{ color: getProductTypeColour(symbol) }}
        >
          {symbol}
        </Typography>
      )
    })

    const aggBidLargerThanAggAsk =
      !Number.isNaN(bestAggBidValue) && Number.isFinite(bestAggBidValue) &&
      !Number.isNaN(bestAggAskValue) && Number.isFinite(bestAggAskValue) &&
      Number(bestAggBidValue) > Number(bestAggAskValue)

    return (
      <Stack direction='row' width={1} marginY={2} sx={{ overflowX: 'auto', justifyContent: { xs: 'revert', sm: 'center' } }}>
        <Stack alignItems='flex-end' paddingX={1} flex='1 0 0px'>
          <Stack direction='row' textAlign='right' p={0} divider={<Divider orientation='vertical' flexItem/>} gap={1}>
            {bestAggBidSymbolNodes}
          </Stack>
          <Stack direction='row' textAlign='right' p={0} divider={<Divider orientation='vertical' flexItem/>} gap={1}>
            {bestPassiveBidSymbolNodes}
          </Stack>
        </Stack>
        <Stack textAlign='right' paddingX={1} flexShrink={0}>
          <Tooltip title='Best Aggregated (Bid)' placement='left'>
            <Typography sx={{ color: aggBidLargerThanAggAsk ? 'success.light' : null }}>{bestAggBidValue}</Typography>
          </Tooltip>
          <Tooltip title='Best Passive (Bid)' placement='left'>
            <Typography>{bestPassiveBidValue}</Typography>
          </Tooltip>
          <Tooltip title='Ref (Bid)' placement='left'>
            <Typography>{refBid}</Typography>
          </Tooltip>
          <Tooltip title='Void (Bid)' placement='left'>
            <Typography>{voidBid}</Typography>
          </Tooltip>
        </Stack>
        <Stack textAlign='center' paddingX={1} flexShrink={0}>
          <Typography fontSize='large' fontWeight='bold' lineHeight={1.3} sx={{ color: aggBidLargerThanAggAsk ? 'success.light' : null }}>
            {midAggValue}
          </Typography>
          <Typography fontSize='large' fontWeight='bold' lineHeight={1.3}>
            {midPassiveValue}
          </Typography>
        </Stack>
        <Stack textAlign='left' paddingX={1} flexShrink={0}>
          <Tooltip title='Best Aggregated (Ask)' placement='right'>
            <Typography sx={{ color: aggBidLargerThanAggAsk ? 'success.light' : null }}>{bestAggAskValue}</Typography>
          </Tooltip>
          <Tooltip title='Best Passive (Ask)' placement='right'>
            <Typography>{bestPassiveAskValue}</Typography>
          </Tooltip>
          <Tooltip title='Ref (Ask)' placement='right'>
            <Typography>{refAsk}</Typography>
          </Tooltip>
          <Tooltip title='Void (Ask)' placement='right'>
            <Typography>{voidAsk}</Typography>
          </Tooltip>
        </Stack>
        <Stack alignItems='flex-start' paddingX={1} flex='1 0 0px'>
          <Stack direction='row' textAlign='left' p={0} divider={<Divider orientation='vertical' flexItem/>} gap={1}>
            {bestAggAskSymbolNodes}
          </Stack>
          <Stack direction='row' textAlign='left' p={0} divider={<Divider orientation='vertical' flexItem/>} gap={1}>
            {bestPassiveAskSymbolNodes}
          </Stack>
        </Stack>
      </Stack>
    )
  }, [mainSummaryData])

  return (
    <Stack>
      {header}

      <DataGridPro
        hideFooter
        slots={{
          toolbar: FilterExportGridToolbar,
        }}
        slotProps={
          (csvBaseFileName == null)
            ? undefined
            : { toolbar: { csvOptions: { fileName: csvBaseFileName + '_strategy_summary', allColumns: true } } }
        }
        autoHeight={true}
        columnHeaderHeight={36}
        rowHeight={24}
        columns={MAIN_SUMMARY_COLUMNS}
        rows={dataGridRows}
        initialState={{
          columns: {
            columnVisibilityModel: {
              'hidden-tgxBBps': false,
              'hidden-tgxABps': false,
              'hidden-init_pos': false,
            },
          },
          sorting: {
            sortModel: [{ field: 'id', sort: 'asc' }],
          },
        }}
        sx={{
          '& .MuiDataGrid-container--top [role=row]': {
            background: 'transparent', // remove inherited container background
          },
          '& .MuiDataGrid-container--bottom [role=row]': {
            background: 'transparent', // remove inherited container background
          },
          '& .riskR.error, & .rUSD-tradability.errorBuy': {
            color: 'error.main',
          },
          '& .isBestAggBidSymbol.bold, & .isBestAggAskSymbol.bold': {
            fontWeight: '900',
            textDecoration: 'wavy underline',
          },
          '& .productType.spot': {
            color: blue[500],
          },
          '& .fundingRate.positive, & .tgxBid.mark, & .tgxAsk.mark': {
            color: 'success.main',
          },
          '& .fundingRate.negative, & .rUSD-tradability.errorSell': {
            color: orange[500],
          },
        }}
      />

      {footer}
    </Stack>
  )
}

export default StrategyComponentMainSummary
